import type { KeyboardEvent, PointerEvent, ReactNode } from "react"
import { useId, useRef, useState } from "react"

const SIZE = 300
const CENTER = SIZE / 2
const RADIUS = CENTER - 32

const pointAt = (angle: number, radius: number): { x: number; y: number } => ({
  x: CENTER + radius * Math.sin((angle * Math.PI) / 180),
  y: CENTER - radius * Math.cos((angle * Math.PI) / 180),
})

export function SavingsDial({
  value,
  min,
  max,
  step,
  size,
  onChange,
  children,
  label,
  valueText,
  interactive = true,
}: {
  value: number
  min: number
  max: number
  step: number
  size: number
  onChange: (next: number) => void
  children: ReactNode
  label: string
  valueText: string
  interactive?: boolean
}): JSX.Element {
  const pointerId = useRef<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const uid = useId().replace(/:/g, ``)
  const fraction = max > min ? (value - min) / (max - min) : 0
  const angle = -135 + fraction * 270
  const handle = pointAt(angle, RADIUS)
  const start = pointAt(-135, RADIUS)
  const arc = `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${
    angle + 135 > 180 ? 1 : 0
  } 1 ${handle.x} ${handle.y}`
  const clamp = (next: number): number => Math.max(min, Math.min(max, next))

  const updateFromPointer = (event: PointerEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect()
    const dx = event.clientX - (rect.left + rect.width / 2)
    const dy = event.clientY - (rect.top + rect.height / 2)
    const nextAngle = Math.max(
      -135,
      Math.min(135, (Math.atan2(dx, -dy) * 180) / Math.PI),
    )
    const steps = ((nextAngle + 135) / 270) * ((max - min) / step)
    const next = clamp(min + Math.round(steps) * step)
    if (next !== value) onChange(next)
  }

  const beginDrag = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0 || pointerId.current != null) return
    event.preventDefault()
    event.currentTarget.focus()
    event.currentTarget.setPointerCapture(event.pointerId)
    pointerId.current = event.pointerId
    setDragging(true)
    updateFromPointer(event)
  }

  const endDrag = (event: PointerEvent<HTMLDivElement>): void => {
    if (pointerId.current !== event.pointerId) return
    pointerId.current = null
    setDragging(false)
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    let next: number
    switch (event.key) {
      case `ArrowRight`:
      case `ArrowUp`:
        next = clamp(value + step)
        break
      case `ArrowLeft`:
      case `ArrowDown`:
        next = clamp(value - step)
        break
      case `Home`:
        next = min
        break
      case `End`:
        next = max
        break
      default:
        return
    }
    event.preventDefault()
    if (next !== value) onChange(next)
  }

  return (
    <div
      role={interactive ? `slider` : `img`}
      aria-label={interactive ? label : `${label}: ${valueText}`}
      aria-valuemin={interactive ? min : undefined}
      aria-valuemax={interactive ? max : undefined}
      aria-valuenow={interactive ? value : undefined}
      aria-valuetext={interactive ? valueText : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? onKeyDown : undefined}
      onPointerDown={interactive ? beginDrag : undefined}
      onPointerMove={(event) => {
        if (pointerId.current === event.pointerId) updateFromPointer(event)
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={() => {
        pointerId.current = null
        setDragging(false)
      }}
      className={`relative aspect-square max-w-full touch-none rounded-full select-none focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none ${
        interactive ? (dragging ? `cursor-grabbing` : `cursor-grab`) : ``
      }`}
      style={{ width: size }}
    >
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="block h-auto w-full overflow-visible"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={`dialBg-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--bp-elevated)" />
            <stop offset="70%" stopColor="var(--bp-surface)" />
            <stop offset="100%" stopColor="var(--bp-page)" />
          </radialGradient>
          <linearGradient id={`dialArc-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--bp-accent)" />
            <stop offset="100%" stopColor="var(--bp-cyan)" />
          </linearGradient>
          <filter
            id={`dialGlow-${uid}`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="4" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle
          cx={CENTER}
          cy={CENTER}
          r={CENTER - 12}
          fill={`url(#dialBg-${uid})`}
          stroke="var(--bp-border)"
        />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={CENTER - 54}
          fill="var(--bp-page)"
          stroke="var(--bp-border)"
        />
        <path
          d={arc}
          fill="none"
          stroke={`url(#dialArc-${uid})`}
          strokeWidth="3"
          strokeLinecap="round"
          opacity="0.65"
        />
        {Array.from({ length: 37 }, (_, index) => {
          const notchAngle = -135 + (index / 36) * 270
          const inner = pointAt(notchAngle, CENTER - 44)
          const outer = pointAt(notchAngle, RADIUS)
          const active = notchAngle <= angle + 0.5
          return (
            <line
              key={notchAngle}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke={active ? `var(--bp-accent)` : `var(--bp-border-strong)`}
              strokeWidth={index % 6 === 0 ? 2 : 1.25}
              strokeLinecap="round"
              opacity={active ? 1 : 0.55}
            />
          )
        })}
        <circle
          cx={handle.x}
          cy={handle.y}
          r={dragging ? 16 : 14}
          fill="var(--bp-accent)"
          stroke="var(--bp-text-soft)"
          strokeWidth="2"
          filter={`url(#dialGlow-${uid})`}
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
        {children}
      </div>
    </div>
  )
}
